import {
  displayTitle,
  type EditorialStatus,
  type LegalStatusValue,
  type ReviewDecision,
  type ReviewDetail,
  type ReviewEvent,
  type SourceReview,
} from '@buakei/types';
import {
  Badge,
  Button,
  DIMENSION_LABELS,
  EmptyState,
  ErrorState,
  LoadingState,
  VerificationDimensions,
  cn,
} from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { caseName, useCases } from '@/pages/library/CasePages';
import { OriginalReader, useCatalogue, useReaderTokens } from '@/pages/library/LibraryPages';
import { ApiError, editorialApi, libraryApi } from '@/services/api';
import { Page } from '@/shell/Page';
import { REPORT_KIND_LABEL } from '@/assistant/ReportProblem';
import { STATUS_LABEL } from './status';

/**
 * The live editorial flow (M3, ADR-023): the real Acts and judgments, their review state, and
 * a review screen with the original beside the extracted text. The server decides who may
 * record what; this screen only offers what the caller's role allows.
 */

const PAGE_SIZE = 50;
const keys = {
  reviews: ['editorial', 'reviews'] as const,
  review: (id: string) => ['editorial', 'review', id] as const,
  openReports: ['editorial', 'reports', 'open'] as const,
  reports: (id: string) => ['editorial', 'reports', id] as const,
};

/**
 * Readers' reports on this document (ADR-026), open ones first. Editors close each with what
 * they found; closing a report records nothing about the verdicts, which are decided above.
 */
function ReportsPanel({
  tokens,
  versionId,
  canClose,
}: {
  tokens: IdentityClient;
  versionId: string;
  canClose: boolean;
}) {
  const queryClient = useQueryClient();
  const reports = useQuery({
    queryKey: keys.reports(versionId),
    queryFn: () => editorialApi.reports(tokens, versionId),
  });
  const [closing, setClosing] = useState<string | null>(null);
  const [status, setStatus] = useState<'resolved' | 'dismissed'>('resolved');
  const [note, setNote] = useState('');
  const statusId = useId();
  const noteId = useId();
  const close = useMutation({
    mutationFn: (id: string) => editorialApi.resolveReport(tokens, id, { status, note }),
    onSuccess: () => {
      setClosing(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: keys.reports(versionId) });
      void queryClient.invalidateQueries({ queryKey: keys.openReports });
    },
  });
  if (reports.isPending) return <LoadingState label="Loading reports" />;
  if (reports.isError) {
    return (
      <ErrorState title="Reports could not be loaded" onRetry={() => void reports.refetch()}>
        {reports.error.message}
      </ErrorState>
    );
  }
  const list = [...reports.data.reports].sort(
    (a, b) => Number(b.status === 'open') - Number(a.status === 'open'),
  );
  if (list.length === 0) return <p className="text-sm text-muted-600">No reports.</p>;
  return (
    <ol className="flex flex-col divide-y divide-line-200">
      {list.map((r) => (
        <li key={r.id} className="flex flex-col gap-1 py-3">
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-navy-900">
            {r.status === 'open' ? (
              <Badge tone="danger">Open</Badge>
            ) : (
              <Badge>{r.status === 'resolved' ? 'Resolved' : 'Dismissed'}</Badge>
            )}
            {REPORT_KIND_LABEL[r.kind]}
          </span>
          <span className="text-xs text-muted-600">
            {new Date(r.createdAt).toLocaleString('en-GB')} · {r.reporterName ?? r.reporterUid} ·
            from{' '}
            {r.context === 'library'
              ? 'the library'
              : r.context === 'answer'
                ? 'an answer'
                : 'a memo'}
            {r.passageId ? ` · passage ${r.passageId}` : ''}
          </span>
          <p className="text-sm whitespace-pre-wrap text-ink-900">{r.description}</p>
          {r.resolution ? (
            <p className="text-sm text-muted-600">
              Closed by {r.resolution.byName ?? r.resolution.byUid}: {r.resolution.note}
            </p>
          ) : null}
          {r.status === 'open' && canClose ? (
            closing === r.id ? (
              <form
                className="mt-2 flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  close.mutate(r.id);
                }}
              >
                <label htmlFor={statusId} className="text-sm font-medium text-ink-900">
                  Outcome
                </label>
                <select
                  id={statusId}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'resolved' | 'dismissed')}
                  className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
                >
                  <option value="resolved">
                    Resolved — the problem was real and is dealt with
                  </option>
                  <option value="dismissed">Dismissed — no problem found</option>
                </select>
                <label htmlFor={noteId} className="text-sm font-medium text-ink-900">
                  What you found
                </label>
                <textarea
                  id={noteId}
                  required
                  minLength={3}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="rounded-md border border-line-200 bg-white p-2 text-ink-900"
                />
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={close.isPending || note.trim().length < 3}
                  >
                    Close the report
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setClosing(null)}
                  >
                    Cancel
                  </Button>
                </div>
                {close.isError ? (
                  <p role="alert" className="text-sm text-ink-900">
                    {close.error.message}
                  </p>
                ) : null}
              </form>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="self-start"
                onClick={() => setClosing(r.id)}
              >
                Close this report
              </Button>
            )
          ) : null}
        </li>
      ))}
    </ol>
  );
}

type QueueItem = {
  versionId: string;
  title: string;
  detail: string;
  collection: 'acts' | 'cases';
  review: SourceReview | undefined;
};

const stateOf = (item: QueueItem): EditorialStatus => item.review?.state ?? 'review_ready';

function reviewedCount(review: SourceReview | undefined) {
  if (!review) return 0;
  return Object.values(review.verification).filter((d) => d.verdict === 'reviewed').length;
}

export function LiveReviewQueue({ tokens }: { tokens: IdentityClient }) {
  const reviews = useQuery({
    queryKey: keys.reviews,
    queryFn: () => editorialApi.reviews(tokens),
  });
  const acts = useCatalogue(tokens);
  const cases = useCases(tokens);
  const openReports = useQuery({
    queryKey: keys.openReports,
    queryFn: () => editorialApi.reports(tokens),
  });
  const reportCounts = useMemo(() => {
    const c = new Map<string, number>();
    for (const r of openReports.data?.reports ?? [])
      c.set(r.versionId, (c.get(r.versionId) ?? 0) + 1);
    return c;
  }, [openReports.data]);
  const [reportedOnly, setReportedOnly] = useState(false);
  const searchId = useId();
  const stateId = useId();
  const collectionId = useId();
  const [query, setQuery] = useState('');
  const [state, setState] = useState<EditorialStatus | 'all'>('all');
  const [collection, setCollection] = useState<'all' | 'acts' | 'cases'>('all');
  const [shown, setShown] = useState(PAGE_SIZE);

  const items = useMemo(() => {
    const byVersion = new Map((reviews.data?.reviews ?? []).map((r) => [r.versionId, r]));
    const all: QueueItem[] = [
      ...(acts.data?.acts ?? []).map((a) => ({
        versionId: a.versionId,
        title: displayTitle(a.title),
        detail: [a.year, a.method === 'ocr' ? 'Scanned (OCR)' : 'Digital text']
          .filter(Boolean)
          .join(' · '),
        collection: 'acts' as const,
        review: byVersion.get(a.versionId),
      })),
      ...(cases.data?.cases ?? []).map((c) => ({
        versionId: c.versionId,
        title: caseName(c),
        detail: [c.citation, c.method === 'ocr' ? 'Scanned (OCR)' : null]
          .filter(Boolean)
          .join(' · '),
        collection: 'cases' as const,
        review: byVersion.get(c.versionId),
      })),
    ];
    // Documents with open reports first, then recent decisions; the rest keep library order.
    return all.sort(
      (a, b) =>
        (reportCounts.get(b.versionId) ?? 0) - (reportCounts.get(a.versionId) ?? 0) ||
        (b.review?.updatedAt ?? '').localeCompare(a.review?.updatedAt ?? '') ||
        0,
    );
  }, [reviews.data, acts.data, cases.data, reportCounts]);

  const counts = useMemo(() => {
    const c = new Map<EditorialStatus, number>();
    for (const item of items) c.set(stateOf(item), (c.get(stateOf(item)) ?? 0) + 1);
    return c;
  }, [items]);

  const needle = query.trim().toLowerCase();
  const visible = items.filter(
    (i) =>
      (state === 'all' || stateOf(i) === state) &&
      (collection === 'all' || i.collection === collection) &&
      (!needle || `${i.title} ${i.detail}`.toLowerCase().includes(needle)) &&
      (!reportedOnly || reportCounts.has(i.versionId)),
  );
  const loading = reviews.isPending || acts.isPending || cases.isPending;
  const failed = reviews.isError || acts.isError || cases.isError;

  return (
    <Page
      title="Editorial review queue"
      description="Each verification dimension is decided separately by a person and kept in the review's history. The AI can never mark a source as reviewed."
      width="wide"
    >
      {loading ? <LoadingState label="Loading the review queue" /> : null}
      {failed ? (
        <ErrorState
          title="The review queue could not be loaded"
          onRetry={() => {
            void reviews.refetch();
            void acts.refetch();
            void cases.refetch();
          }}
        >
          {(reviews.error ?? acts.error ?? cases.error)?.message}
        </ErrorState>
      ) : null}
      {!loading && !failed ? (
        <>
          <p className="text-sm text-ink-900">
            {items.length.toLocaleString()} documents:{' '}
            {(Object.keys(STATUS_LABEL) as EditorialStatus[])
              .filter((s) => counts.get(s))
              .map(
                (s) => `${counts.get(s)!.toLocaleString()} ${STATUS_LABEL[s].label.toLowerCase()}`,
              )
              .join(' · ')}
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex min-w-[16rem] flex-1 flex-col gap-1">
              <label htmlFor={searchId} className="text-sm font-medium text-navy-900">
                Find a document
              </label>
              <input
                id={searchId}
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShown(PAGE_SIZE);
                }}
                placeholder="Title, party, citation or year"
                className="h-10 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={collectionId} className="text-sm font-medium text-navy-900">
                Collection
              </label>
              <select
                id={collectionId}
                value={collection}
                onChange={(e) => {
                  setCollection(e.target.value as typeof collection);
                  setShown(PAGE_SIZE);
                }}
                className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
              >
                <option value="all">Acts and judgments</option>
                <option value="acts">Acts</option>
                <option value="cases">Judgments</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={stateId} className="text-sm font-medium text-navy-900">
                Review state
              </label>
              <select
                id={stateId}
                value={state}
                onChange={(e) => {
                  setState(e.target.value as EditorialStatus | 'all');
                  setShown(PAGE_SIZE);
                }}
                className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
              >
                <option value="all">All states</option>
                {(Object.keys(STATUS_LABEL) as EditorialStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s].label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex h-10 items-center gap-2 text-sm text-ink-900">
              <input
                type="checkbox"
                className="size-4 accent-navy-900"
                checked={reportedOnly}
                onChange={(e) => {
                  setReportedOnly(e.target.checked);
                  setShown(PAGE_SIZE);
                }}
              />
              Only documents with open reports ({reportCounts.size})
            </label>
          </div>
          {visible.length === 0 ? (
            <EmptyState title="No documents match">
              Not found within the library with these filters.
            </EmptyState>
          ) : (
            <div
              role="region"
              aria-label="Documents for review"
              tabIndex={0}
              className="overflow-x-auto rounded-lg border border-line-200 bg-white"
            >
              <table className="w-full min-w-[40rem] text-left text-sm">
                <caption className="sr-only">Documents for review</caption>
                <thead className="border-b border-line-200 bg-soft-white text-navy-900">
                  <tr>
                    <th scope="col" className="p-3 font-semibold">
                      Document
                    </th>
                    <th scope="col" className="p-3 font-semibold">
                      Collection
                    </th>
                    <th scope="col" className="p-3 font-semibold">
                      State
                    </th>
                    <th scope="col" className="p-3 font-semibold">
                      Dimensions reviewed
                    </th>
                    <th scope="col" className="p-3 font-semibold">
                      Last decision
                    </th>
                    <th scope="col" className="p-3 font-semibold">
                      Open reports
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-200">
                  {visible.slice(0, shown).map((i) => {
                    const s = STATUS_LABEL[stateOf(i)];
                    return (
                      <tr key={i.versionId}>
                        <td className="p-3">
                          <Link
                            to={`/editorial/review/${encodeURIComponent(i.versionId)}`}
                            className="font-medium text-navy-900 underline-offset-2 hover:underline"
                          >
                            {i.title}
                          </Link>
                          {i.detail ? (
                            <span className="block text-xs text-muted-600">{i.detail}</span>
                          ) : null}
                        </td>
                        <td className="p-3 text-ink-900">
                          {i.collection === 'acts' ? 'Act' : 'Judgment'}
                        </td>
                        <td className="p-3">
                          <Badge tone={s.tone}>{s.label}</Badge>
                        </td>
                        <td className="p-3 text-ink-900">{reviewedCount(i.review)} of 4</td>
                        <td className="p-3 text-muted-600">
                          {i.review?.updatedAt
                            ? new Date(i.review.updatedAt).toLocaleDateString('en-GB')
                            : 'None'}
                        </td>
                        <td className="p-3 text-ink-900">
                          {reportCounts.get(i.versionId) ? (
                            <Badge tone="danger">{reportCounts.get(i.versionId)}</Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {visible.length > shown ? (
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => setShown((n) => n + PAGE_SIZE)}
            >
              Show more ({(visible.length - shown).toLocaleString()} left)
            </Button>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}

type Choice = 'authenticity' | 'text_integrity' | 'legal_status' | 'legal_analysis' | 'state';

const VERDICTS: Record<Exclude<Choice, 'state'>, { value: string; label: string }[]> = {
  authenticity: [
    { value: 'reviewed', label: 'Reviewed — the document is what it claims to be' },
    { value: 'rejected', label: 'Rejected — not authentic or not the official text' },
    { value: 'pending', label: 'Pending — reset to not yet reviewed' },
  ],
  text_integrity: [
    { value: 'reviewed', label: 'Reviewed — the extracted text matches the original' },
    { value: 'rejected', label: 'Rejected — the extracted text is wrong or garbled' },
    { value: 'pending', label: 'Pending — reset to not yet reviewed' },
  ],
  legal_status: [
    { value: 'reviewed', label: 'Reviewed — status established as of a date' },
    { value: 'unknown', label: 'Could not be established' },
    { value: 'pending', label: 'Pending — reset to not yet reviewed' },
  ],
  legal_analysis: [
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'disputed', label: 'Disputed' },
    { value: 'not_applicable', label: 'Not applicable' },
    { value: 'pending', label: 'Pending — reset to not yet reviewed' },
  ],
};

const LEGAL_STATUS_LABEL: Record<LegalStatusValue, string> = {
  in_force: 'In force',
  partly_in_force: 'Partly in force',
  not_commenced: 'Not commenced',
  repealed: 'Repealed',
  historical: 'Historical version',
};

const STATE_CHOICES: {
  value: Extract<ReviewDecision, { kind: 'state' }>['state'];
  approve: boolean;
}[] = [
  { value: 'under_review', approve: false },
  { value: 'approved_for_defined_use', approve: true },
  { value: 'provisional', approve: true },
  { value: 'needs_more_evidence', approve: true },
  { value: 'rejected', approve: true },
];

function DecisionForm({
  tokens,
  versionId,
  detail,
}: {
  tokens: IdentityClient;
  versionId: string;
  detail: ReviewDetail;
}) {
  const queryClient = useQueryClient();
  const choices: Choice[] = [
    ...(detail.canReview ? (['authenticity', 'text_integrity'] as const) : []),
    ...(detail.canApprove ? (['legal_status', 'legal_analysis'] as const) : []),
    'state',
  ];
  const [choice, setChoice] = useState<Choice>(choices[0]!);
  const [verdict, setVerdict] = useState('reviewed');
  const [status, setStatus] = useState<LegalStatusValue>('in_force');
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [state, setState] = useState(
    STATE_CHOICES.find((c) => detail.canApprove || !c.approve)!.value,
  );
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const ids = { choice: useId(), verdict: useId(), status: useId(), asOf: useId(), note: useId() };

  const decide = useMutation({
    mutationFn: (decision: ReviewDecision) => editorialApi.decide(tokens, versionId, decision),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.review(versionId), updated);
      void queryClient.invalidateQueries({ queryKey: keys.reviews });
      void queryClient.invalidateQueries({ queryKey: ['library', 'reviews'] });
      setNote('');
      setDone('Decision recorded.');
    },
  });
  const conflict = decide.error instanceof ApiError && decide.error.status === 409;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setDone(null);
    const expectedRevision = detail.review.revision;
    const decision: ReviewDecision =
      choice === 'state'
        ? { kind: 'state', state, note, expectedRevision }
        : choice === 'legal_status'
          ? {
              kind: 'dimension',
              dimension: 'legal_status',
              verdict: verdict as 'pending' | 'reviewed' | 'unknown',
              ...(verdict === 'reviewed' ? { status, asOf } : {}),
              note,
              expectedRevision,
            }
          : choice === 'legal_analysis'
            ? {
                kind: 'dimension',
                dimension: 'legal_analysis',
                verdict: verdict as 'pending' | 'reviewed' | 'disputed' | 'not_applicable',
                note,
                expectedRevision,
              }
            : {
                kind: 'dimension',
                dimension: choice,
                verdict: verdict as 'pending' | 'reviewed' | 'rejected',
                note,
                expectedRevision,
              };
    decide.mutate(decision);
  };

  const inputClass =
    'h-10 w-full min-w-0 rounded-md border border-line-200 bg-white px-2 text-ink-900';
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <h2 className="font-serif text-lg font-semibold text-navy-900">Record a decision</h2>
      <div className="flex flex-col gap-1">
        <label htmlFor={ids.choice} className="text-sm font-medium text-ink-900">
          Deciding
        </label>
        <select
          id={ids.choice}
          value={choice}
          onChange={(e) => {
            setChoice(e.target.value as Choice);
            setVerdict('reviewed');
          }}
          className={inputClass}
        >
          {choices.map((c) => (
            <option key={c} value={c}>
              {c === 'state' ? 'Review state' : DIMENSION_LABELS[c].name}
            </option>
          ))}
        </select>
      </div>
      {choice === 'state' ? (
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.verdict} className="text-sm font-medium text-ink-900">
            New state
          </label>
          <select
            id={ids.verdict}
            value={state}
            onChange={(e) => setState(e.target.value as typeof state)}
            className={inputClass}
          >
            {STATE_CHOICES.filter((c) => detail.canApprove || !c.approve).map((c) => (
              <option key={c.value} value={c.value}>
                {STATUS_LABEL[c.value].label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.verdict} className="text-sm font-medium text-ink-900">
            Verdict
          </label>
          <select
            id={ids.verdict}
            value={verdict}
            onChange={(e) => setVerdict(e.target.value)}
            className={inputClass}
          >
            {VERDICTS[choice].map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-muted-600">{DIMENSION_LABELS[choice].help}</p>
        </div>
      )}
      {choice === 'legal_status' && verdict === 'reviewed' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor={ids.status} className="text-sm font-medium text-ink-900">
              Status
            </label>
            <select
              id={ids.status}
              value={status}
              onChange={(e) => setStatus(e.target.value as LegalStatusValue)}
              className={inputClass}
            >
              {(Object.keys(LEGAL_STATUS_LABEL) as LegalStatusValue[]).map((s) => (
                <option key={s} value={s}>
                  {LEGAL_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={ids.asOf} className="text-sm font-medium text-ink-900">
              As of
            </label>
            <input
              id={ids.asOf}
              type="date"
              required
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <label htmlFor={ids.note} className="text-sm font-medium text-ink-900">
          Evidence or reason
        </label>
        <textarea
          id={ids.note}
          required
          minLength={3}
          maxLength={2000}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What you checked, against what, and anything a later reviewer should know"
          className="rounded-md border border-line-200 bg-white p-2 text-ink-900 placeholder:text-muted-600"
        />
      </div>
      <Button
        type="submit"
        className="self-start"
        disabled={decide.isPending || note.trim().length < 3}
      >
        {decide.isPending ? 'Recording…' : 'Record decision'}
      </Button>
      <p role="status" aria-live="polite" className="text-sm text-ink-900">
        {done}
      </p>
      {decide.isError ? (
        <div role="alert" className="flex flex-col gap-2 text-sm text-ink-900">
          <p>{decide.error.message}</p>
          {conflict ? (
            <Button
              size="sm"
              variant="secondary"
              className="self-start"
              onClick={() => {
                decide.reset();
                void queryClient.invalidateQueries({ queryKey: keys.review(versionId) });
              }}
            >
              Reload the review
            </Button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

function eventSummary(e: ReviewEvent): string {
  if (e.kind === 'state') return `Review state: ${STATUS_LABEL[e.toState ?? 'under_review'].label}`;
  const name = e.dimension ? DIMENSION_LABELS[e.dimension].name : 'Dimension';
  const verdict = e.verdict ? e.verdict.replace(/_/g, ' ') : '';
  const status = e.status
    ? ` — ${LEGAL_STATUS_LABEL[e.status]}${e.asOf ? `, as of ${e.asOf}` : ''}`
    : '';
  return `${name}: ${verdict}${status}`;
}

function History({ events }: { events: ReviewEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-600">No decisions yet.</p>;
  }
  return (
    <ol className="flex flex-col divide-y divide-line-200">
      {[...events].reverse().map((e) => (
        <li key={e.id} className="flex flex-col gap-1 py-3">
          <span className="text-sm font-medium text-navy-900">{eventSummary(e)}</span>
          <span className="text-xs text-muted-600">
            {new Date(e.at).toLocaleString('en-GB')} · {e.actorName ?? e.actorUid} (
            {e.actorRole === 'legal_reviewer' ? 'Legal reviewer' : 'Legal editor'})
          </span>
          <p className="text-sm whitespace-pre-wrap text-ink-900">{e.note}</p>
        </li>
      ))}
    </ol>
  );
}

function ExtractedText({ tokens, versionId }: { tokens: IdentityClient; versionId: string }) {
  const version = useQuery({
    queryKey: ['library', 'version', versionId],
    queryFn: () => libraryApi.version(tokens, versionId),
  });
  if (version.isPending) return <LoadingState label="Loading the extracted text" />;
  if (version.isError) {
    return (
      <ErrorState
        title="The extracted text could not be loaded"
        onRetry={() => void version.refetch()}
      >
        {version.error.message}
      </ErrorState>
    );
  }
  const passages = version.data.passages;
  if (passages.length === 0) return <EmptyState title="No extracted text" />;
  return (
    <div
      role="region"
      aria-label="Extracted text"
      tabIndex={0}
      className="h-[80vh] overflow-y-auto rounded-lg border border-line-200 bg-white p-4"
    >
      {passages.map((p) => (
        <section key={p.id} className="flex flex-col gap-1 border-b border-line-200 py-3">
          <h3 className="text-sm font-semibold text-navy-900">
            {p.label}{' '}
            <span className="font-normal text-muted-600">
              (page {p.pageStart}
              {p.pageEnd !== p.pageStart ? `–${p.pageEnd}` : ''})
            </span>
          </h3>
          <p className="text-sm whitespace-pre-wrap text-ink-900">{p.text}</p>
        </section>
      ))}
    </div>
  );
}

export function ReviewPage() {
  const tokens = useReaderTokens();
  const { versionId = '' } = useParams();
  const [view, setView] = useState<'original' | 'text'>('original');
  const detail = useQuery({
    queryKey: keys.review(versionId),
    queryFn: () => editorialApi.review(tokens!, versionId),
    enabled: tokens !== null,
  });
  if (!tokens) {
    return (
      <Page title="Review">
        <EmptyState title="Sign in with an editorial account to review sources" />
      </Page>
    );
  }
  const review = detail.data?.review;
  const s = review ? STATUS_LABEL[review.state] : null;
  return (
    <Page
      title={review ? displayTitle(review.title) : 'Review'}
      description={
        review ? (
          <span className="flex flex-wrap items-center gap-2">
            {s ? <Badge tone={s.tone}>{s.label}</Badge> : null}
            <span>{review.collection === 'acts' ? 'Act' : 'Judgment'}</span>
          </span>
        ) : undefined
      }
      width="wide"
    >
      <Link
        to="/editorial"
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> Back to the review queue
      </Link>
      {detail.isPending ? <LoadingState label="Loading the review" /> : null}
      {detail.isError ? (
        <ErrorState title="This review could not be loaded" onRetry={() => void detail.refetch()}>
          {detail.error.message}
        </ErrorState>
      ) : null}
      {detail.data && review ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
          <div className="flex min-w-0 flex-col gap-3">
            <div role="group" aria-label="Show" className="flex gap-2">
              {(
                [
                  ['original', 'Original'],
                  ['text', 'Extracted text'],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={view === value ? 'primary' : 'secondary'}
                  aria-pressed={view === value}
                  onClick={() => setView(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
            {view === 'original' ? (
              <OriginalReader tokens={tokens} doc={{ versionId, title: review.title }} />
            ) : (
              <ExtractedText tokens={tokens} versionId={versionId} />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            <section aria-label="Verification" className="flex flex-col gap-2">
              <h2 className="font-serif text-lg font-semibold text-navy-900">Verification</h2>
              <VerificationDimensions verification={review.verification} />
            </section>
            {detail.data.canReview || detail.data.canApprove ? (
              <DecisionForm tokens={tokens} versionId={versionId} detail={detail.data} />
            ) : null}
            <section aria-label="Reports" className="flex flex-col gap-2">
              <h2 className="font-serif text-lg font-semibold text-navy-900">Reports</h2>
              <ReportsPanel
                tokens={tokens}
                versionId={versionId}
                canClose={detail.data.canReview || detail.data.canApprove}
              />
            </section>
            <section aria-label="History" className={cn('flex flex-col gap-2')}>
              <h2 className="font-serif text-lg font-semibold text-navy-900">History</h2>
              <History events={detail.data.events} />
            </section>
          </div>
        </div>
      ) : null}
    </Page>
  );
}
